import lookupTimeZone from "@photostructure/tz-lookup";
import { getMatchCoordinates } from "./matchCoordinates";
import { matchHasEnded, venueDay } from "./matchTime";

const validZone = value => {
  if (!value || typeof value !== "string") return null;
  try { new Intl.DateTimeFormat("en", { timeZone: value }).format(); return value; } catch { return null; }
};

// Public-page presentation only. Never spread the inferred zone or phase into
// a match update, director form, registration decision, or device sync payload.
export function presentationTimeZone(match) {
  const configured = validZone(match?.timeZone) || validZone(match?.competitorInfo?.timeZone);
  if (configured) return configured;
  const point = getMatchCoordinates(match);
  if (point) {
    try { return validZone(lookupTimeZone(point.latitude, point.longitude)) || "UTC"; } catch {}
  }
  return "UTC";
}

const midnightCache = new Map();
function venueMidnight(day, zone) {
  const utc = Date.parse(`${day}T00:00:00Z`);
  if (!Number.isFinite(utc) || new Date(utc).toISOString().slice(0, 10) !== day) return null;
  const key = `${day}:${zone}`;
  if (midnightCache.has(key)) return midnightCache.get(key);
  // Find the beginning of the venue calendar day, including daylight-saving
  // changes at midnight. Never use the visiting competitor's browser zone.
  let low = utc - 36 * 3600000, high = utc + 36 * 3600000;
  while (low < high) {
    const mid = Math.floor((low + high) / 2);
    if (venueDay(new Date(mid), zone) < day) low = mid + 1;
    else high = mid;
  }
  const instant = venueDay(new Date(low), zone) === day ? low : null;
  if (midnightCache.size >= 256) midnightCache.clear();
  midnightCache.set(key, instant);
  return instant;
}

export function presentationStart(match) {
  const value = match?.startDate || match?.matchDate;
  if (!value || typeof value !== "string") return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return venueMidnight(value, presentationTimeZone(match));
  // Saved timestamp starts carry Z/an offset. Do not interpret a malformed,
  // offset-free timestamp in the visitor's local time zone.
  if (!/[Tt].*(?:[Zz]|[+-]\d{2}:?\d{2})$/.test(value)) return null;
  const instant = Date.parse(value);
  return Number.isFinite(instant) ? instant : null;
}

export function matchPresentationPhase(match, now = new Date()) {
  if (!match) return "scheduled";
  const status = String(match.status || "scheduled").toLowerCase();
  if (match.lifecycle?.cancelledAt || ["cancelled", "canceled"].includes(status)) return "cancelled";
  if (["completed", "final", "official", "closed"].includes(status)) return "completed";
  if (matchHasEnded({ ...match, timeZone: presentationTimeZone(match) }, now)) return "completed";
  const start = presentationStart(match);
  return status === "in_progress" || (start !== null && start <= now.getTime()) ? "in_progress" : "scheduled";
}
